import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  ExternalLink,
  Loader2,
  Save,
  Server,
  Trash2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import type { AssistantPatch } from "@/lib/platformApi";
import type { PlatformAssistant } from "@/lib/platformTypes";
import { store, useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { ConnectPlatformCard, EmptyState, ViewLoading, ViewSection, relativeTime } from "@/views/shared";
import { statusVariant } from "@/views/assistants/AssistantsView";

interface FormValues {
  name: string;
  model: string;
  systemPrompt: string;
  maxTokens: string;
  maxSteps: string;
  rateLimitPerMinute: string;
}

function toFormValues(a: PlatformAssistant): FormValues {
  return {
    name: a.name,
    model: a.model ?? "",
    systemPrompt: a.systemPrompt ?? "",
    maxTokens: a.maxTokens != null ? String(a.maxTokens) : "",
    maxSteps: a.maxSteps != null ? String(a.maxSteps) : "",
    rateLimitPerMinute: a.rateLimitPerMinute != null ? String(a.rateLimitPerMinute) : "",
  };
}

function parsePositiveInt(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

// Only changed fields go on the wire (snake_case per AssistantPatch). Cleared
// numeric fields are omitted rather than sent as 0/undefined: update_assistant
// has no unset semantics.
function buildPatch(baseline: FormValues, form: FormValues): AssistantPatch {
  const patch: AssistantPatch = {};
  if (form.name !== baseline.name && form.name.trim()) patch.name = form.name.trim();
  if (form.model !== baseline.model && form.model.trim()) patch.model = form.model.trim();
  if (form.systemPrompt !== baseline.systemPrompt) patch.system_prompt = form.systemPrompt;
  if (form.maxTokens !== baseline.maxTokens) {
    const n = parsePositiveInt(form.maxTokens);
    if (n !== undefined) patch.max_tokens = n;
  }
  if (form.maxSteps !== baseline.maxSteps) {
    const n = parsePositiveInt(form.maxSteps);
    if (n !== undefined) patch.max_steps = n;
  }
  if (form.rateLimitPerMinute !== baseline.rateLimitPerMinute) {
    const n = parsePositiveInt(form.rateLimitPerMinute);
    if (n !== undefined) patch.rate_limit_per_minute = n;
  }
  return patch;
}

function InlineError({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 break-words">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="shrink-0 text-destructive/70 hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function MonoLink({ href, label }: { href: string; label: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <span className="shrink-0 text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex min-w-0 items-center gap-1 font-mono text-xs text-primary hover:underline"
      >
        <span className="truncate">{href}</span>
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    </div>
  );
}

function DeleteAssistantDialog({
  assistant,
  pending,
  error,
  onConfirm,
}: {
  assistant: PlatformAssistant;
  pending: boolean;
  error?: string;
  onConfirm: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const match = typed === assistant.name;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        setOpen(next);
        if (!next) setTyped("");
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2 />
          Delete assistant
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="glass-border max-w-[calc(100vw-2rem)] rounded-xl sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {assistant.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the assistant, its endpoint, and its chat page. Type{" "}
            <span className="font-mono text-foreground">{assistant.name}</span> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={assistant.name}
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
        />
        {error && (
          <p className="break-words text-sm text-destructive">{error}</p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!match || pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              // Radix closes the dialog on action click; deletion is async and
              // may fail, so keep it open until the store call resolves.
              e.preventDefault();
              void onConfirm()
                .then(() => setOpen(false))
                .catch(() => {});
            }}
          >
            {pending ? <Loader2 className="animate-spin" /> : <Trash2 />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AssistantDetailView({ id }: { id: string }) {
  const { platform, errors } = useStore();
  const { available, assistants, pendingAction } = platform;

  // Older deep links may carry the slug where the id belongs; both are unique.
  const assistant =
    assistants.items.find((a) => a.id === id) ?? assistants.items.find((a) => a.slug === id);

  const [seededId, setSeededId] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<FormValues | null>(null);
  const [form, setForm] = useState<FormValues | null>(null);

  // Seed-during-render (React's adjust-state-on-prop-change pattern) so the
  // form resets exactly once per assistant, not on every store update.
  if (assistant && seededId !== assistant.id) {
    const values = toFormValues(assistant);
    setSeededId(assistant.id);
    setBaseline(values);
    setForm(values);
  }

  useEffect(() => {
    if (available && !assistants.loaded) {
      void store.loadPlatformOverview();
    }
  }, [available, assistants.loaded]);

  if (!available) return <ConnectPlatformCard />;
  if (!assistants.loaded) return <ViewLoading />;

  if (!assistant) {
    return (
      <div className="mx-auto w-full max-w-3xl flex-1 overflow-y-auto p-4">
        <EmptyState
          message="Assistant not found. It may have been deleted."
          action={
            <Button variant="outline" size="sm" onClick={() => store.navigate({ name: "assistants" })}>
              <ArrowLeft />
              All assistants
            </Button>
          }
        />
      </div>
    );
  }

  if (!form || !baseline) return null;

  const updateErrorKey = `platform:update-assistant:${assistant.id}`;
  const deleteErrorKey = `platform:delete-assistant:${assistant.id}`;
  const updateError = errors[updateErrorKey];
  const deleteError = errors[deleteErrorKey];
  const saving = pendingAction === `update-assistant:${assistant.id}`;
  const deleting = pendingAction === `delete-assistant:${assistant.id}`;

  const patch = buildPatch(baseline, form);
  const dirty = Object.keys(patch).length > 0;

  const setField = (field: keyof FormValues) => (value: string) =>
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));

  async function handleSave() {
    if (!assistant || !form || !dirty) return;
    store.clearError(updateErrorKey);
    try {
      await store.updateAssistant(assistant.id, patch);
      // The update response truncates systemPrompt to 200 chars, so re-seeding
      // from the store would clobber the full prompt just saved. The submitted
      // values become the new baseline instead.
      setBaseline(form);
    } catch {
      // recorded under errors[updateErrorKey] by runPlatformAction
    }
  }

  async function handleDelete() {
    if (!assistant) return;
    store.clearError(deleteErrorKey);
    await store.deleteAssistant(assistant.id);
    store.navigate({ name: "assistants" });
  }

  return (
    <div className="mx-auto w-full max-w-3xl flex-1 overflow-y-auto p-4">
      <Button
        variant="ghost"
        size="sm"
        className="mb-3 -ml-2 text-muted-foreground"
        onClick={() => store.navigate({ name: "assistants" })}
      >
        <ArrowLeft />
        Assistants
      </Button>

      <div className="glass-border mb-6 rounded-xl border p-4">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 shrink-0 text-primary" />
          <h1 className="min-w-0 truncate text-base font-semibold">{assistant.name}</h1>
          <Badge variant={statusVariant(assistant.status)} className="ml-auto shrink-0">
            {assistant.status ?? "unknown"}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono text-xs text-muted-foreground">{assistant.slug}</span>
          {assistant.updatedAt && (
            <span className="text-xs text-muted-foreground">updated {relativeTime(assistant.updatedAt)}</span>
          )}
        </div>
        <Separator className="my-3" />
        <div className="space-y-1.5">
          <MonoLink href={assistant.endpoint} label="endpoint" />
          {assistant.chatUrl && <MonoLink href={assistant.chatUrl} label="chat" />}
        </div>
      </div>

      <ViewSection title="Configuration">
        <div className="glass-border space-y-4 rounded-xl border p-4">
          {updateError && <InlineError message={updateError} onDismiss={() => store.clearError(updateErrorKey)} />}

          <div className="space-y-1.5">
            <Label htmlFor="assistant-name">Name</Label>
            <Input
              id="assistant-name"
              value={form.name}
              onChange={(e) => setField("name")(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="assistant-model">Model</Label>
            <Input
              id="assistant-model"
              value={form.model}
              onChange={(e) => setField("model")(e.target.value)}
              placeholder="anthropic/claude-sonnet-5"
              spellCheck={false}
              className="font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="assistant-prompt">System prompt</Label>
            <Textarea
              id="assistant-prompt"
              value={form.systemPrompt}
              onChange={(e) => setField("systemPrompt")(e.target.value)}
              rows={10}
              spellCheck={false}
              className="font-mono text-xs leading-relaxed"
            />
            {/* list_assistants truncates the prompt to 200 chars; without a
                store-level get_assistant loader this form may start from that
                truncated text. */}
            {baseline.systemPrompt.length === 200 && (
              <p className="text-xs text-muted-foreground">
                The prompt shown may be truncated to 200 characters by the list API. Saving replaces
                the full prompt with the text above.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="assistant-max-tokens">Max tokens</Label>
              <Input
                id="assistant-max-tokens"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.maxTokens}
                onChange={(e) => setField("maxTokens")(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assistant-max-steps">Max steps</Label>
              <Input
                id="assistant-max-steps"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.maxSteps}
                onChange={(e) => setField("maxSteps")(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assistant-rate-limit">Rate limit / min</Label>
              <Input
                id="assistant-rate-limit"
                type="number"
                inputMode="numeric"
                min={1}
                value={form.rateLimitPerMinute}
                onChange={(e) => setField("rateLimitPerMinute")(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" disabled={!dirty || saving} onClick={() => void handleSave()}>
              {saving ? <Loader2 className="animate-spin" /> : <Save />}
              Save changes
            </Button>
          </div>
        </div>
      </ViewSection>

      {assistant.mcpServers.length > 0 && (
        <ViewSection title="MCP servers">
          {/* Read-only: mcp_servers is patchable, but structured URL/header
              editing needs its own form; deploy_assistant remains the way to
              change these for now. */}
          <div className="glass-border divide-y divide-border rounded-xl border">
            {assistant.mcpServers.map((s) => (
              <div key={s.url} className="flex min-w-0 items-center gap-2 p-3">
                <Server className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{s.name}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground">{s.url}</p>
                </div>
                {s.hasAuth && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    auth
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </ViewSection>
      )}

      <ViewSection title="Danger zone">
        <div className={cn("rounded-xl border border-destructive/40 p-4", "glass-border")}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Deleting an assistant takes its endpoint offline immediately.
            </p>
            <DeleteAssistantDialog
              assistant={assistant}
              pending={deleting}
              error={deleteError}
              onConfirm={handleDelete}
            />
          </div>
          {deleteError && (
            <div className="mt-3">
              <InlineError message={deleteError} onDismiss={() => store.clearError(deleteErrorKey)} />
            </div>
          )}
        </div>
      </ViewSection>
    </div>
  );
}
